import { Dialog, DialogFooter, DialogType, ProgressIndicator, Toggle } from "@fluentui/react";
import { DefaultButton, PrimaryButton } from "@fluentui/react/lib/Button";
import { useBoolean, useId } from "@fluentui/react-hooks";
import { FormulasMode } from "@taskpane/utils";
import { throttle } from "lodash";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import ChoiceFormulaWrapper from "../../features/choiceFormulaWrapper";
import ComputeFormulas from "../../features/computeFormulas";
import { GLBalanceFormula } from "../../features/glBalanceFormula";
import { useStore } from "@taskpane/hooks";

let currentCellFormula = null;
const cancellableComputeJob = { cancel: null, cancelled: false};

export const ComputePage: React.FunctionComponent = () => {
  const { t } = useTranslation();
  const [isComputing, setIsComputing] = useState(true);
  const [currentSheetIdentifier, setCurrentSheetIdentifier] = useState(null);
  const [percentComplete, setPercentComplete] = useState(0);
  const { token } = useStore();

  const displayFormulas = false;
  const [viewCellDetailsEnabled, setViewCellDetailsEnabled] = useState(false);

  const [hideDialog, { toggle: toggleHideDialog }] = useBoolean(true);

  useEffect(() => {
    if (isComputing) {

      const compute = new ComputeFormulas(token);
      let maxProgress = 0;
      const throttledProgress = throttle((current) => {
        setPercentComplete(current / maxProgress);
      }, 400);

      Excel.run(async (context) => {
        try {
          cancellableComputeJob.cancelled = false;
          await compute.compute(
            displayFormulas ? FormulasMode.Formulas : FormulasMode.Amount,
            (max, currentSheetIdentifier1) => {
              maxProgress = max;
              setCurrentSheetIdentifier(currentSheetIdentifier1);
            },
            (current) => {
              throttledProgress(current);
            },
            () => {
              setPercentComplete(0);
              setCurrentSheetIdentifier(null);
              setIsComputing(false);
            },
            cancellableComputeJob,
            context
          );
        } catch (e) {
          console.error("ComputePage ", e);
        }
      });
    }
  }, [displayFormulas, isComputing]);


  function onStopClick() {
    setIsComputing(false);
    cancellableComputeJob.cancel();
  }

  useEffect(() => {
    Office.context.document.addHandlerAsync(Office.EventType.DocumentSelectionChanged, async function () {
      await Excel.run(async (context) => {
        const cell = context.workbook.getActiveCell();
        cell.load("formulas");
        await context.sync();
        currentCellFormula = cell.formulas.length > 0 && cell.formulas[0].length > 0 ? cell.formulas[0][0] : null;
        const w = new ChoiceFormulaWrapper();
        const unwrappedFormula = w.unwrap(currentCellFormula);
        if (unwrappedFormula) {
          const f = new GLBalanceFormula(null);
          if (f.parse(unwrappedFormula.formula)) setViewCellDetailsEnabled(true);
        }
      });
    });
  }, []);

  const dialogContentProps = {
    type: DialogType.normal,
    title: t("confirmationTitle"),
    closeButtonAriaLabel: t("close"),
    subText: t("confirmationText"),
  };

  const labelId: string = useId("dialogLabel");
  const subTextId: string = useId("subTextLabel");

  const dialogStyles = { main: { maxWidth: 450 } };

  const modalProps = React.useMemo(
    () => ({
      titleAriaId: labelId,
      subtitleAriaId: subTextId,
      isBlocking: false,
      styles: dialogStyles,
      dragOptions: undefined,
    }),
    [labelId, subTextId]
  );

  const rest = (
    <>
      <hr style={{ margin: "20px 0" }}></hr>

      <Dialog
        hidden={hideDialog}
        onDismiss={toggleHideDialog}

        dialogContentProps={dialogContentProps}
        modalProps={modalProps}
      >
        <DialogFooter>
          <PrimaryButton onClick={() => { toggleHideDialog(); setIsComputing(true) } } text="OK" />
          <DefaultButton onClick={toggleHideDialog} text={t("cancel")} />
        </DialogFooter>
      </Dialog>
      {/*<a href="#" onClick={onViewCellDetailsClick}>t("displayDetails")</a>*/}
    </>
  );
  if (isComputing) {
    return (
      <div className="pt-4">
        <div className="mt-1 mb-2 ms-fontWeight-bold">
          <span>{t("computePage")}</span>
        </div>
        <ProgressIndicator
          barHeight={10}
          description={currentSheetIdentifier}
          percentComplete={percentComplete}
        ></ProgressIndicator>
        <DefaultButton onClick={() => onStopClick()}>{t("stop")}</DefaultButton>
        {rest}
      </div>
    );
  } else {
    return (
      <div className="pt-4">
        <div className="mt-1 mb-2 ms-fontWeight-bold">
          <span>{t("computePage")}</span>
        </div>
        <DefaultButton onClick={() => setIsComputing(true)}>{t("compute")}</DefaultButton>
        {rest}
      </div>
    );
  }
};
